import type { JSONContent } from '@tiptap/core';

const BLOCK_CONTAINER_TYPES = new Set([
  'doc',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'taskList',
  'taskItem',
  'table',
  'tableRow',
  'tableCell',
  'tableHeader',
]);

/** Plain-text projection of rich text (e.g. combobox display/search). */
export function richTextToPlainText(content: JSONContent | null | undefined): string {
  if (!content) return '';
  const walk = (node: JSONContent): string => {
    if (typeof node.text === 'string') return node.text;
    if (node.type === 'hardBreak' || node.type === 'horizontalRule') return ' ';
    const separator = node.type && BLOCK_CONTAINER_TYPES.has(node.type) ? ' ' : '';
    return node.content?.map(walk).join(separator) ?? '';
  };
  return walk(content).replace(/\s+/g, ' ').trim();
}

export function isRichTextContentEmpty(content: JSONContent | null | undefined): boolean {
  if (!content || typeof content !== 'object') {
    return true;
  }

  if (content.type === 'text') {
    return !content.text || content.text.trim() === '';
  }

  if (!content.content || content.content.length === 0) {
    return true;
  }

  return content.content.every((child) => isRichTextContentEmpty(child));
}

export function sanitizeRichTextContent(content: JSONContent | null | undefined): JSONContent {
  if (!content || typeof content !== 'object') {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }

  const sanitizeNode = (node: JSONContent): JSONContent | null => {
    if (node.type === 'text' && (!node.text || node.text === '')) {
      return null;
    }

    if (node.content && Array.isArray(node.content)) {
      const sanitizedContent = node.content
        .map(sanitizeNode)
        .filter((child): child is JSONContent => child !== null);

      return {
        ...node,
        content: sanitizedContent.length > 0 ? sanitizedContent : undefined,
      };
    }

    return node;
  };

  return sanitizeNode(content) || { type: 'doc', content: [{ type: 'paragraph' }] };
}
